import React, { FormEvent, useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import AuthContext from "../../../contexts/AuthContext";
import { RouteProps } from "react-router-dom";

import Input from "../../../components/Form/Input";
import Button from "../../../components/Form/Button";
import Select from "../../../components/Form/Select";

import Message from "./Message";
import { SetModal } from "../../../components/Modal";

import { SimpleResponse } from "../../../services/api/Request";
import Actual, { IActual, IChannel, IStatus } from "../../../services/api/Actual";
import WeekPeriod from "../../../services/api/WeekPeriod";

interface FormProps extends RouteProps {
  actual: IActual;
  month: number;
  year: number;
}

const Form: React.FC<FormProps> = ({ actual, month, year }) => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const readOnlyStatus = ["Viewer", "Sender"].indexOf(user!.profile.name) > -1;
  const readOnlyForm = ["Viewer", "Approver"].indexOf(user!.profile.name) > -1;
  const readOnlyApproved = actual.status.slug === 'approved' && user!.profile.name !== 'Administrator';
  const [response, setResponse] = useState<SimpleResponse>();
  const [status, setStatus] = useState<IStatus[]>([]);
  const [loading, isLoading] = useState<Boolean>(true);
  const [weekPeriod, setWeekPeriod] = useState<boolean[]>([false, false, false, false]);

  const isUpdate = actual && actual.id > 0;

  useEffect(() => {
    Promise.all([Actual.GetAllStatus(), WeekPeriod.GetActual()])
      .then((value) => {
        const day = new Date().getDate();
        setStatus(value[0]);
        setWeekPeriod([
          day >= value[1].startDateW1 && day <= value[1].endDateW1
        ]);

        isLoading(false);
        getTotalValue();
      })
      .catch(() => undefined);
  }, []);

  async function handleFormSubmit(event: FormEvent) {
    event.preventDefault();
    const formData: FormData = new FormData(event.target as HTMLFormElement);
    let data: any = {
      groupId: actual.groupId,
      channels: []
    };

    formData.forEach((value: FormDataEntryValue, key: string) => {
      if (key.indexOf("channel") === -1) data[key] = value;
    });

    actual.channels.forEach((value: IChannel, key: number) => {
      const elName: string = "channel" + value.channelId;
      const fw1: number | null = normalizeNumber(formData.get(elName + "[actual]"));


      data["channels"].push({
        channelId: value.channelId,
        channelName: value.channelName,
        channelTypeSlug: value.channelTypeSlug,
        id: value.id,
        actual: fw1 ? fw1 : 0,
      });
    });



    try {
      const response: SimpleResponse = await (isUpdate
        ? Actual.Update(actual.id, data)
        : Actual.Create(year, month, data));
      setResponse(response);
    } catch (error) {
    }
  }

  function normalizeNumber(value: FormDataEntryValue | null) {
    return value === null ? null : +(value + "").replace(/\./g, '');
  }

  async function handleFormSubmitStatus(event: FormEvent) {
    event.preventDefault();
    const formData: FormData = new FormData(event.target as HTMLFormElement);

    let data: any = {
      Id: formData.get("status")
    };

    const response: SimpleResponse = await Actual.SetStatus(
      actual.id,
      data
    );
    setResponse(response);
  }

  function listActualChannels() {
    function itemActualChannel(value: any) {
      return (
        <Input
          masked={true}
          disabled={readOnlyForm || readOnlyApproved}
          readOnly={readOnlyStatus && !weekPeriod[0]}
          key={
            "channel" +
            value.channelId +
            "[actual" +
            "]"
          }
          name={
            "channel" +
            value.channelId +
            "[actual" +
            "]"
          }
          data-channel-type={value.segment}
          title={value.channelName}
          value={value["actual"]}
          type="number"
        />
      );
    }

    const filterChannelType = (channelTypeSlug: string) =>
      actual.channels.filter(item => item.channelTypeSlug === channelTypeSlug);

    return (
      <div className="form form--border">
        <h3 className="form__title">Actual</h3>
        <div className="segment-container">
          <p className="form__subtitle">Segment</p>
          <div className="form__fields--sideBySide">
            {filterChannelType('segment').map(itemActualChannel)}
            <div className="form-group">
              <label className="form-label form__label form__total">Total Segment: <span>0</span></label>
            </div>
          </div>
        </div>
        <div className="source-container">
          <p className="form__subtitle">Source</p>
          <div className="form__fields--sideBySide">
            {filterChannelType('source').map(itemActualChannel)}
            <div className="form-group">
              <label className="form-label form__label form__total">Total Source: <span>0</span></label>
            </div>
          </div>
        </div>
        <div className="key-accounts-container">
          <p className="form__subtitle">Key Accounts</p>
          <div className="form__fields--sideBySide">
            {filterChannelType('key-accounts').map(itemActualChannel)}
            <div className="form-group">
              <label className="form-label form__label form__total">Total Key Accounts: <span>0</span></label>
            </div>
          </div>
        </div>
      </div>

    );
  }

  //#region Calculate total
  function setTotalValue(box: Element) {
    let total = 0;
    box.querySelectorAll(".form-group input").forEach((input: Element) => {
      const _value = parseFloat(("0" + input.getAttribute("value")).replace(/\D+/gi, ''));
      total += _value;
    });

    let formTotal = box.querySelector(".form__total span");
    if (formTotal)
      formTotal.innerHTML = (total + "").toString().replace(/\B(?=(\d{3})+(?!\d))/gi, "."); // format thousands
  }

  function getTotalValue() {
    document.querySelectorAll(".form__group-container .form .form__fields--sideBySide").forEach(box => {
      setTotalValue(box); // calculate total when the page reloads

      box.querySelectorAll(".form-group input").forEach((input: Element) => {
        input.addEventListener("keyup", (event) => {
          setTotalValue(box); // calculate total when the field changed
        })
      });
    });
  }
  //#endregion

  return (
    <React.Fragment>
      {response ? SetModal.FromResponse(response, () => {
        setResponse(undefined);
        if (response.status === 200) {
          history.push({ pathname: "/empty" });
          history.goBack();
        }
      }) : null}
      {loading ? (
        <span>Loading...</span>
      ) : (
        <React.Fragment>
          <form
            className="container details__form"
            onSubmit={handleFormSubmitStatus}
          >
            <div className="details__select">
              <Select
                disabled={readOnlyStatus || readOnlyApproved || actual.id === 0}
                labelClass="form__label"
                name="status"
                title="Status"
                options={status.map((obj) => {
                  return { value: obj.id, label: obj.name };
                })}
                defaultValue={actual.status.id}
              />
              {readOnlyStatus || readOnlyApproved || actual.id === 0 ? null : (
                <Button
                  className="button button--big button--blue details__button--form"
                  title="Apply"
                />
              )}
            </div>
          </form>

          <form
            className="container details__forms form__group-container"
            onSubmit={handleFormSubmit}
          >
            {listActualChannels()}
            {(readOnlyForm || readOnlyApproved) ? null : (
              <Button
                className="button button--big button--blue details__button--form"
                title={isUpdate ? "Save" : "Send"}
              />
            )}
          </form>
        </React.Fragment>
      )}
      <Message actualId={actual.id} groupId={actual.groupId} monthId={month} currentYear={year} />
    </React.Fragment>
  );
};

export default Form;
